import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Loading from '../loading/Loading'

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Pagination from '@mui/material/Pagination'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Autocomplete from '@mui/material/Autocomplete'

import { AdminManage } from '../styles'

import { PAGES } from '../navigation/routes'
import { PAGE_SIZE } from '../websiteConfig'

import AdminTagModal from '../modals/AdminTagModal'
import TagSelector from '../tags/TagSelector'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { initializeTags, createTag, deleteTag } from '../../reducers/tagsReducers'

const TAG_TYPES = [
    { key: 'contentCategory', label: 'Content Categories' },
    { key: 'content', label: 'Content' },
    { key: 'medicalSpecialty', label: 'Medical Specialty' },
    { key: 'experience', label: 'Experience' },
    { key: 'demographic', label: 'Demographic' },
    { key: 'adminCategory', label: 'Admin Category' },
    { key: 'tcairem', label: 'T-CAIREM Type' },
    { key: 'default', label: 'Glossary' },
]

const ManageTags = () => {
    const [filter, setFilter] = useState('')
    const [name, setName] = useState('')
    const [category, setCategory] = useState('')
    const [personas, setPersonas] = useState([])
    const [sort, setSort] = useState(1)
    const [tagsToShow, setTagsToShow] = useState(null)
    const [activeTagType, setActiveTagType] = useState('contentCategory')

    const tagsByType = useSelector(state => state.tags || {})
    const [searchTags, setSearchTags] = useState(null)
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const pageSize = PAGE_SIZE
    const [page, setPage] = useState(1)

    // Content tag categories from the dedicated model
    const contentCategories = useMemo(() => {
        const cats = tagsByType?.contentCategory || []
        return [...cats].sort((a, b) => a.name.localeCompare(b.name))
    }, [tagsByType?.contentCategory])

    const sortNames = useCallback((type, currentTags) => {
        const arr = [...(currentTags || [])]
        if(type) {
            setTagsToShow(arr.sort((a, b) => a.name.localeCompare(b.name)))
        } else {
            setTagsToShow(arr.sort((a, b) => b.name.localeCompare(a.name)))
        }
        setSort(type ? 0 : 1)
    }, [])

    // Initialize all tag types. RequireStaff (App.js) guarantees this
    // component only mounts for a loaded, staff user — this check just
    // guards against `user` being momentarily stale on the very first
    // effect run.
    useEffect(() => {
        if (user?.staff) {
            TAG_TYPES.forEach(({ key }) => {
                if (tagsByType?.[key] === undefined) {
                    dispatch(initializeTags(key))
                }
            })
        }
    }, [user, dispatch, tagsByType])

    // Update displayed tags when active type changes or tags arrive
    useEffect(() => {
        const current = tagsByType?.[activeTagType] || []
        sortNames(1, current)
        setPage(1)
    }, [activeTagType, tagsByType, sortNames])

    useEffect(() => {
        if(tagsToShow) setSearchTags(tagsToShow.filter(tag => tag.name.toLowerCase().includes(filter.toLowerCase())))
    }, [tagsToShow, filter])

    // Clamp the current page whenever the filtered result set shrinks (e.g. a
    // filter narrows the results) so the table body never slices past the
    // end of `searchTags` while the Pagination control still reports an
    // earlier page (issue #396). Runs on every `searchTags` change, not just
    // filter keystrokes, so it also self-corrects if the underlying tag list
    // changes size for other reasons (e.g. async tag data arriving) while a
    // filter is already active.
    useEffect(() => {
        if(searchTags === null) return
        const maxPage = Math.max(1, Math.ceil(searchTags.length / pageSize))
        setPage(current => (current > maxPage ? maxPage : current))
    }, [searchTags, pageSize])


    // reset fields to original values if user wants to
    const reset = () => {
        setName('')
        setCategory('')
        setPersonas([])
    }

    const handleCreate = (event) => {
        event.preventDefault()

        if (activeTagType === 'tcairem' && personas.length === 0) {
            dispatch(setErrorNotification('Select at least one persona'))
            return
        }

        const data = {
            name: name,
        }

        if (activeTagType === 'content' && category) {
            data.category = category
        }

        if (activeTagType === 'tcairem') {
            data.personas = personas
        }

        dispatch(createTag(data, user.staff, activeTagType))
        reset()
    }

    const handleDelete = (tag) => {
        const message = tag.resource_count === undefined
            ? `Delete the tag "${tag.name}"? This removes it from every resource it is applied to and cannot be undone.`
            : `Delete the tag "${tag.name}"? It is applied to ${tag.resource_count} resource(s); this removes it from all of them and cannot be undone.`
        if (window.confirm(message)) {
            dispatch(deleteTag(tag.id, user.staff, activeTagType))
        }
    }

    if(searchTags === null || tagsToShow === null) {
        return (
            <Container {...AdminManage.loading.props} sx={AdminManage.loading.style}>
                <Loading />
            </Container>
        )
    }

    return (
        <Container sx={AdminManage.container}>
            <Typography {...AdminManage.title} gutterBottom>
                {PAGES.manage_tags.title}
            </Typography>
            <Tabs
                value={activeTagType}
                onChange={(e, val) => { setActiveTagType(val); setCategory(''); setPersonas([]) }}
                sx={{ mb: 2 }}
                variant="scrollable"
                scrollButtons="auto"
            >
                {TAG_TYPES.map(tab => (
                    <Tab key={tab.key} value={tab.key} label={tab.label} />
                ))}
            </Tabs>
            <Typography {...AdminManage.subheading} sx={AdminManage.subheadingMT} gutterBottom>Create Tag</Typography>

            <Box sx={AdminManage.tagForm}>
                <form onSubmit={handleCreate}>
                    <Grid container spacing={2} sx={{alignItems: 'center'}}>
                        <Grid item xs={12} sm={6} paddingBottom={3}>
                            <TextField
                                sx={{width: '100%'}}
                                value={name}
                                onChange={e => setName(e.target.value)}
                                label="Tag"
                                required
                            />
                        </Grid>
                        {activeTagType === 'content' && (
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={contentCategories}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={contentCategories.find(c => c.id === category) || null}
                                    onChange={(_, newValue) => setCategory(newValue ? newValue.id : '')}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Category (optional)"
                                            helperText="Select a category for this content tag"
                                            sx={{width: '100%'}}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        {activeTagType === 'tcairem' && (
                            <Grid item xs={12} sm={6}>
                                <TagSelector
                                    tagType='tcairemPersona'
                                    label='Personas'
                                    selectedTags={personas}
                                    onChange={setPersonas}
                                    required
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <Button type='submit'  variant='contained' autoFocus>
                                Create Tag
                            </Button>

                        </Grid>
                    </Grid>
                </form>
            </Box>
            <Typography {...AdminManage.subheading} gutterBottom>Search Tags</Typography>
            <Typography {...AdminManage.subheading2} gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        sx={{marginBottom: '20px'}}
                        fullWidth
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        label="Filter By Tag"
                    />
                </Grid>
            </Grid>
            <TableContainer component={Paper}>
                <Table sx={AdminManage.table} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <IconButton onClick={() => sortNames(sort, tagsToShow)} aria-label="Sort by name">

                                    {!sort ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                                </IconButton>
                                Name
                            </TableCell>
                            {activeTagType === 'content' && (
                                <TableCell>Category</TableCell>
                            )}
                            {activeTagType === 'tcairem' && (
                                <TableCell>Personas</TableCell>
                            )}
                            <TableCell {...AdminManage.columnAlign}>Edit</TableCell>
                            <TableCell {...AdminManage.columnAlign}>Delete</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {searchTags.slice((page-1)*pageSize, (page-1)*pageSize+pageSize).map(tag => (
                            <TableRow
                                key={`${tag.id}`}
                                sx={AdminManage.tableRow}
                            >
                                <TableCell {...AdminManage.tableCell.props} sx={AdminManage.tableCell.style}>
                                    {tag.name}
                                </TableCell>
                                {activeTagType === 'content' && (
                                    <TableCell {...AdminManage.tableCell.props} sx={AdminManage.tableCell.style}>
                                        {tag.category_name || '—'}
                                    </TableCell>
                                )}
                                {activeTagType === 'tcairem' && (
                                    <TableCell {...AdminManage.tableCell.props} sx={AdminManage.tableCell.style}>
                                        {(tag.personas || []).join(', ') || '—'}
                                    </TableCell>
                                )}
                                <TableCell {...AdminManage.columnAlign}>
                                    <AdminTagModal
                                        tag={tag}
                                        activeTagType={activeTagType}
                                        contentCategories={contentCategories}
                                    />
                                </TableCell>
                                <TableCell {...AdminManage.columnAlign}>
                                    <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={()=>handleDelete(tag)}>
                                        Delete Tag
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Pagination sx={AdminManage.pagination} count={Math.ceil(searchTags.length/pageSize)} page={page} onChange={(event, value) => setPage(value)} />
        </Container>
    )
}

export default ManageTags
